/**
 * Feature E06-F04 — "Library & History браузер" request/response contracts.
 *
 * This is a NEW (stage-2 Company Research) surface — it has no n8n legacy node;
 * the canonical spec is the prototype `research.proto.js` (renderLibrary /
 * renderHistory / openRequest / cloneRequest) + company-research-architecture
 * §6.6 and the backlog "Library & History браузер" item. The logic ported here
 * is the prototype behaviour verbatim, expressed over the v2 Prisma models
 * `companies` and `research_requests` (schema.prisma, migration 023).
 *
 * Two entry points behind ONE list command (`research_list`, scope-discriminated):
 *
 *   GET /research/list?scope=library&… → rows from `companies`  (browse EVERY company)
 *   GET /research/list?scope=history&… → rows from `research_requests` (past runs)
 *
 * plus a clone action that pre-fills the Intake form from a past run's criteria:
 *
 *   POST /research/list/clone  { sourceRequestId } → { ok, draft }  (new draft request)
 *
 * Lives in its OWN file (NOT re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// `RESEARCH_REQUEST_STATUSES`/`researchRequestStatusSchema`/`ResearchRequestStatus`
// are owned by `research-intake.ts` (the request lifecycle contract) and are
// byte-identical here. Re-export from there so there is ONE definition (no TS2308).
//
// `intakeCriteriaSchema`/`IntakeCriteria` is the TRUE persisted shape of
// `research_requests.criteria` (see BUG-16): the E06 intake create service writes
// the machine-valued IntakeCriteria (purpose:'sourcing'|'market_mapping',
// IntakeLocation[] objects, nullable founded/size numbers, nested gradeDefinitions),
// NOT the display-cased legacy blob `researchCriteriaSchema` (jsonb.ts). The clone
// endpoint copies that column verbatim, so its response criteria MUST be validated
// against the same schema `researchGetResponseSchema` already uses for a read.
import { RESEARCH_REQUEST_STATUSES, researchRequestStatusSchema, intakeCriteriaSchema, } from './research-intake.js';
export { RESEARCH_REQUEST_STATUSES, researchRequestStatusSchema };
// ---------------------------------------------------------------------------
// scope + paging
// ---------------------------------------------------------------------------
/** The two list entry points (T01: scope='library' | 'history'). */
export const RESEARCH_LIST_SCOPES = ['library', 'history'];
export const researchListScopeSchema = z.enum(RESEARCH_LIST_SCOPES);
/** Default page size for both scopes (T01 acceptance #5: 20). */
export const RESEARCH_LIST_PAGE_SIZE = 20;
/** Hard upper bound so a hostile `page_size` cannot dump the whole table. */
export const RESEARCH_LIST_MAX_PAGE_SIZE = 100;
// ---------------------------------------------------------------------------
// company entity-type (companies.entity_type) — drives the Library badge
// ---------------------------------------------------------------------------
/**
 * `companies.entity_type` values (schema.prisma:353). Anything other than
 * `pure-play` renders a badge in the Library row ("division of <parent>" /
 * "portfolio · <parent>", T02 acceptance #4).
 */
export const COMPANY_ENTITY_TYPES = ['pure-play', 'division-of', 'portfolio-item'];
export const companyEntityTypeSchema = z.enum(COMPANY_ENTITY_TYPES);
/** Human label for the entity badge ("" for pure-play → no badge). */
export const COMPANY_ENTITY_BADGE_PREFIX = {
    'pure-play': '',
    'division-of': 'division of',
    'portfolio-item': 'portfolio ·',
};
// ---------------------------------------------------------------------------
// status → screen routing (T04 "open-by-status")
// ---------------------------------------------------------------------------
// `research_requests.status` values (schema.prisma:402) are re-exported from
// `research-intake.ts` above (RESEARCH_REQUEST_STATUSES / researchRequestStatusSchema
// / ResearchRequestStatus) — single source of truth for the request lifecycle.
/** The destination screens a History row can open into (prototype `openRequest`). */
export const RESEARCH_SCREENS = ['intake', 'run', 'review'];
/**
 * Status → screen map (T04 acceptance, verbatim from prototype `opensTo`):
 *   draft                  → intake (Request)
 *   queued | running       → run
 *   failed                 → run (failed state, same screen)
 *   in_review | published  → review
 *   cancelled              → intake (re-open as a fresh draft target)
 */
export const RESEARCH_STATUS_OPENS_TO = {
    draft: 'intake',
    queued: 'run',
    running: 'run',
    failed: 'run',
    in_review: 'review',
    published: 'review',
    cancelled: 'intake',
};
/** Capitalised screen label for the "Open → <screen>" button (T04 acceptance #4). */
export const RESEARCH_SCREEN_LABELS = {
    intake: 'Request',
    run: 'Run',
    review: 'Review',
};
/** Pure resolver — status → target screen (the only place the table is read). */
export function opensToScreen(status) {
    const parsed = researchRequestStatusSchema.safeParse(status);
    return parsed.success ? RESEARCH_STATUS_OPENS_TO[parsed.data] : 'intake';
}
/** Pure helper — the button caption ("Open → Run" etc.) for a request status. */
export function openButtonLabel(status) {
    return `Open → ${RESEARCH_SCREEN_LABELS[opensToScreen(status)]}`;
}
// ---------------------------------------------------------------------------
// row DTOs
// ---------------------------------------------------------------------------
/**
 * Library row — one company (T01 scope='library', T02 columns). Mirrors the
 * `companies` projection: name/domain/industry/sub/size/hq/founded/source +
 * usage + freshness + entity badge data.
 */
export const libraryCompanyRowSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    domain: z.string().nullable(),
    industry: z.string().nullable(),
    sub_industry: z.string().nullable(),
    size_band: z.string().nullable(),
    hq: z.string().nullable(),
    founded: z.number().int().nullable(),
    source: z.string().nullable(),
    /** ISO 8601 or null — drives the "verified <date>" + "Verified <90 days" stat. */
    last_verified_at: z.string().nullable(),
    /** Reuse counter (used_in_count× pill). */
    used_in_count: z.number().int(),
    entity_type: companyEntityTypeSchema,
    /** Display name of the parent company (for the division/portfolio badge), or null. */
    parent: z.string().nullable(),
});
/**
 * E06-F06-T01 — which request field a History search word matched ("by what it
 * was found" chips). `brief` is the free text of the criteria (an excerpt is sent).
 */
export const RESEARCH_SEARCH_MATCH_FIELDS = [
    'title',
    'client',
    'industry',
    'subcategory',
    'location',
    'competitor',
    'company_type',
    'brief',
];
export const researchSearchMatchFieldSchema = z.enum(RESEARCH_SEARCH_MATCH_FIELDS);
/** One "found by" chip: the field and the matched value (original casing). */
export const researchSearchMatchSchema = z.object({
    field: researchSearchMatchFieldSchema,
    value: z.string(),
});
/**
 * History row — one past research run (T01 scope='history'). Mirrors a
 * `research_requests` projection plus the derived long-list count.
 */
export const historyRequestRowSchema = z.object({
    id: z.number().int(),
    title: z.string(),
    /** One-line criteria summary (industry · locations · target). */
    criteria_summary: z.string(),
    /** Number of companies on the run's long-list (research_companies count). */
    count: z.number().int(),
    status: researchRequestStatusSchema,
    sheet_url: z.string().nullable(),
    /** Linked project title, or null for a standalone run. */
    project: z.string().nullable(),
    /** ISO 8601 — run creation date. */
    date: z.string(),
    /** Pre-computed routing target so the UI never re-derives it (T04). */
    opens_to: z.enum(RESEARCH_SCREENS),
    /**
     * E06-F06-T01 — "found by" chips for a `q` search: each criteria field + value a
     * query word matched. Empty when no `q` was given. Computed on the server.
     */
    matches: z.array(researchSearchMatchSchema).default([]),
});
// ---------------------------------------------------------------------------
// Library stat cards (T03 acceptance #3)
// ---------------------------------------------------------------------------
/**
 * The four Library stat-cards: Companies in library / Cache hit-rate /
 * Industries covered / Verified <90 days.
 */
export const libraryStatsSchema = z.object({
    companies_in_library: z.number().int(),
    /** Percentage 0..100 (used_in_count>0 reuse share — the "cache hit-rate"). */
    cache_hit_rate: z.number(),
    industries_covered: z.number().int(),
    verified_under_90_days: z.number().int(),
});
// ---------------------------------------------------------------------------
// request: GET /research/list query
// ---------------------------------------------------------------------------
/**
 * The filter block (T01: { industry, q, status }). `q` searches name/domain/
 * industry/sub-industry (library) or title/criteria (history); `status` only
 * applies to the history scope.
 */
export const researchListFilterSchema = z.object({
    industry: z.string().trim().min(1).optional(),
    q: z.string().trim().min(1).optional(),
    status: researchRequestStatusSchema.optional(),
    /**
     * E06-F04-T07 — library scope only: list/count just the companies approved in a
     * closed review (Submit review / published). Absent/false = the whole table.
     */
    approvedOnly: z.boolean().optional(),
});
/**
 * Query string for GET /research/list. `page`/`page_size` are coerced from the
 * string query; the filter keys are flattened (q/industry/status) so the URL is
 * `?scope=library&q=acme&industry=Fintech&approved_only=true&page=2`.
 */
export const researchListQuerySchema = z.object({
    scope: researchListScopeSchema,
    q: z.string().trim().min(1).optional(),
    industry: z.string().trim().min(1).optional(),
    status: researchRequestStatusSchema.optional(),
    /** E06-F04-T07 — `approved_only=true` (or `1`) = the "Approved only" Library mode. */
    approved_only: z
        .enum(['true', 'false', '1', '0'])
        .optional()
        .transform((v) => v === 'true' || v === '1'),
    page: z.coerce.number().int().positive().default(1),
    page_size: z.coerce
        .number()
        .int()
        .positive()
        .max(RESEARCH_LIST_MAX_PAGE_SIZE)
        .default(RESEARCH_LIST_PAGE_SIZE),
});
// ---------------------------------------------------------------------------
// response: GET /research/list
// ---------------------------------------------------------------------------
/** Common envelope head shared by both scopes (T01 acceptance #4). */
const listEnvelopeBase = {
    ok: z.literal(true),
    page: z.number().int(),
    page_size: z.number().int(),
    total: z.number().int(),
};
/** scope='library' response: company rows + the four stat-cards. */
export const libraryListResponseSchema = z.object({
    ...listEnvelopeBase,
    scope: z.literal('library'),
    rows: z.array(libraryCompanyRowSchema),
    stats: libraryStatsSchema,
});
/** scope='history' response: past-run rows. */
export const historyListResponseSchema = z.object({
    ...listEnvelopeBase,
    scope: z.literal('history'),
    rows: z.array(historyRequestRowSchema),
});
/** GET /research/list — discriminated on `scope`. */
export const researchListResponseSchema = z.discriminatedUnion('scope', [
    libraryListResponseSchema,
    historyListResponseSchema,
]);
// ---------------------------------------------------------------------------
// clone (T05)
// ---------------------------------------------------------------------------
/** POST /research/list/clone body — the run to copy criteria from. */
export const cloneRequestSchema = z.object({
    sourceRequestId: z.coerce.number().int().positive(),
});
/**
 * Clone result — a READ-ONLY prefill copied from the source request (E12-F17-T04).
 * No row is persisted by the clone action: the response carries the source id,
 * a suggested '<title> (copy)' title and the source `criteria`, and the web Intake
 * screen pre-fills its chips/pills/toggles from it (T05 acceptance #3). The request
 * row is created exactly once, later, by Run / Save draft (`POST /research/create`).
 * Before E12-F17-T04 every click persisted a '(copy)' draft row (orphans in History);
 * the persisted `draft.id` is therefore gone from the contract. The source request is
 * never mutated (T05 acceptance #4).
 *
 * `criteria` is the machine-valued {@link IntakeCriteria} — the exact shape the
 * intake create service persists into `research_requests.criteria` and the clone
 * service copies verbatim. BUG-16: it was previously typed as the display-cased
 * legacy blob `researchCriteriaSchema`, which no real row uses (Stage-2 research was
 * never in legacy prod), so every clone of a real request failed response validation
 * with a 400 (lowercase `purpose` enum, `null` founded/size numbers, object
 * `locations`). The Intake form now consumes this pass-through directly (no
 * blob→machine remap).
 */
export const cloneResponseSchema = z.object({
    ok: z.literal(true),
    prefill: z.object({
        /** The request the criteria were copied from (never a new row). */
        sourceRequestId: z.number().int(),
        /** Suggested Intake title: '<source title> (copy)'. */
        title: z.string(),
        criteria: intakeCriteriaSchema,
        /** Always 'intake' — a clone opens the Intake screen. */
        opens_to: z.literal('intake'),
    }),
});
// ---------------------------------------------------------------------------
// similar lists (E06-F06-T02)
// ---------------------------------------------------------------------------
/** Max similar lists returned (spec: «не больше 5 результатов»). */
export const SIMILAR_LISTS_LIMIT = 5;
/** Minimum score shown (spec: «порог показа — 0,3»). */
export const SIMILAR_LISTS_MIN_SCORE = 0.3;
/** How many most recent requests are scored (spec: «последние 1000 заявок»). */
export const SIMILAR_LISTS_CANDIDATES = 1000;
/**
 * Weight of each draft criteria field in the similarity score (spec: индустрия ×3,
 * сабкатегории ×2, локации ×2, конкуренты ×1, типы компаний ×1).
 */
export const SIMILAR_LISTS_WEIGHTS = {
    industry: 3,
    subcategory: 2,
    location: 2,
    competitor: 1,
    company_type: 1,
};
/** A location value of a draft: `{label}` (IntakeLocation) or a bare string (old drafts). */
const similarLocationSchema = z.union([z.string(), z.object({ label: z.string() }).passthrough()]);
/**
 * POST /research/list/similar body — the criteria of the Intake form being filled
 * (+ the id of the request being edited, which is excluded from the result).
 */
export const similarListsRequestSchema = z.object({
    industry: z.string().max(500).default(''),
    subCategories: z.array(z.string().max(500)).max(200).default([]),
    locations: z.array(similarLocationSchema).max(200).default([]),
    competitors: z.array(z.string().max(500)).max(200).default([]),
    companyTypes: z.array(z.string().max(500)).max(200).default([]),
    /** The request being edited (an edit of an existing one) — never suggested to itself. */
    excludeRequestId: z.coerce.number().int().positive().nullable().optional(),
});
/** One similar past list (spec «Карточка»). */
export const similarListRowSchema = z.object({
    requestId: z.number().int(),
    title: z.string(),
    /** Linked project label (Project.role_name), or null for a standalone run. */
    projectLabel: z.string().nullable(),
    /** ISO 8601 — request creation date. */
    createdAt: z.string(),
    /** Long-list size (research_companies rows). */
    companies: z.number().int(),
    /** Companies approved in review (review_status = accepted). */
    accepted: z.number().int(),
    /** Weighted similarity 0..1 (≥ SIMILAR_LISTS_MIN_SCORE). */
    score: z.number().min(0).max(1),
    /** The matched values per field (original casing of the found list). */
    matches: z.array(researchSearchMatchSchema),
    status: researchRequestStatusSchema,
    /** Pre-computed routing target for the «Открыть» action (same as History). */
    opens_to: z.enum(RESEARCH_SCREENS),
});
/** POST /research/list/similar response — up to 5 rows, score desc then date desc. */
export const similarListsResponseSchema = z.object({
    ok: z.literal(true),
    rows: z.array(similarListRowSchema).max(SIMILAR_LISTS_LIMIT),
});
//# sourceMappingURL=research-library.js.map